/**
 * SlideList
 * @class
 * @constructor
 * @public
 * @version 1.0.0
 */
class SlideList extends HTMLElement {
	constructor() {
		super();

		this.next = this.next.bind(this);
		this.prev = this.prev.bind(this);
		this.updateSliderPos = this.updateSliderPos.bind(this);

		this.prefersReducedMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)"
		).matches;

		console.log(this.prefersReducedMotion);

		// Media queries for responsive behavior
		this.mediaQuery = window.matchMedia("(max-width: 768px)");
		this.isMobile = this.mediaQuery.matches;
	}

	/**
	 * Getters
	 */

	/**
	 * @type string
	 */
	get buttonNextSelector() {
		return this.getAttribute("data-button-next-selector") || "";
	}

	/**
	 * @type string
	 */
	get buttonPreviousSelector() {
		return this.getAttribute("data-button-previous-selector") || "";
	}

	/**
	 * @type string
	 */
	get buttonNextContent() {
		return this.getAttribute("data-button-next-content") || "›";
	}

	/**
	 * @type string
	 */
	get buttonPreviousContent() {
		return this.getAttribute("data-button-previous-content") || "‹";
	}

	/**
	 * @type string
	 */
	get buttonNextClasses() {
		return (
			this.getAttribute("data-button-next-classes") ||
			"slide-list__button slide-list__button--next"
		);
	}

	/**
	 * @type string
	 */
	get buttonPreviousClasses() {
		return (
			this.getAttribute("data-button-previous-classes") ||
			"slide-list__button slide-list__button--previous"
		);
	}

	/**
	 * @type string
	 */
	get buttonNextAriaLabel() {
		return this.getAttribute("data-button-next-aria-label") || "";
	}

	/**
	 * @type string
	 */
	get buttonPreviousAriaLabel() {
		return this.getAttribute("data-button-previous-aria-label") || "";
	}

	/**
	 * @type string
	 */
	get buttonNextAriaHidden() {
		return this.getAttribute("data-button-next-aria-hidden") || "true";
	}

	/**
	 * @type string
	 */
	get buttonPreviousAriaHidden() {
		return this.getAttribute("data-button-previous-aria-hidden") || "true";
	}

	/**
	 * @type HTMLElement
	 */
	get slider() {
		return /** @type {HTMLElement} */ (this.querySelector(
			this.getAttribute("data-slider-selector") || ".slider"
		));
	}

	/**
	 * @type HTMLElement[]
	 */
	get sliderItems() {
		return Array.from(
			/** @type HTMLCollectionOf<HTMLElement> */ this.slider.children
		);
	}

	/**
	 * @type HTMLElement
	 */
	get nearestElement() {
		const viewport = this.getBoundingClientRect();
		let element = this.sliderItems[0];
		let current = -1;
		this.sliderItems.forEach(slide => {
			const rect = slide.getBoundingClientRect();
			// Calculate visible area of the slide within the container
			const visible = Math.max(
				0,
				Math.min(rect.right, viewport.right) -
					Math.max(rect.left, viewport.left)
			);
			if (visible > current) {
				element = slide;
				current = visible;
			}
		});
		return /** @type {HTMLElement} */ (element);
	}

	/**
	 * @type number
	 */
	get nearestIndex() {
		return this.sliderItems.indexOf(this.nearestElement) || 0;
	}

	/**
	 * connectedCallback
	 * Render html/css & attatch event listeners
	 */
	connectedCallback() {
		this.createProgressIndicator();
		if (this.buttonNextSelector && this.buttonPreviousSelector) {
			this.buttonNext = /** @type {HTMLButtonElement} */ (document.querySelector(
				this.buttonNextSelector
			));
			this.buttonPrevious = /** @type {HTMLButtonElement} */ (document.querySelector(
				this.buttonPreviousSelector
			));
		} else {
			this.createButtons();
		}
		this.setupButtons();

		this.slider.addEventListener("scroll", this.updateSliderPos);
		this.resizeObserver = new ResizeObserver(
			this.throttle(this.updateSliderPos, 200)
		);
		if (this.progress) {
			this.resizeObserver.observe(this.progress);
		}
	}

	/**
	 * disconnectedCallback
	 * Remove listeners.
	 */
	disconnectedCallback() {
		this.slider.removeEventListener("scroll", this.updateSliderPos);
		this.resizeObserver?.disconnect();
		this.progress?.remove();

		if (this.buttonNextSelector && this.buttonPreviousSelector) {
			this.buttonNext?.removeEventListener("click", this.next);
			this.buttonPrevious?.removeEventListener("click", this.prev);
		} else {
			this.buttonNext?.remove();
			this.buttonPrevious?.remove();
		}
	}

	/**
	 * Creates next / previous buttons
	 */
	createButtons() {
		this.buttonNext = document.createElement("button");
		this.buttonPrevious = document.createElement("button");

		this.buttonNext.innerText = this.buttonNextContent;
		this.buttonPrevious.innerText = this.buttonPreviousContent;

		if (this.buttonNextAriaLabel) {
			this.buttonNext.setAttribute("aria-label", this.buttonNextAriaLabel);
		}
		if (this.buttonPreviousAriaLabel) {
			this.buttonPrevious.setAttribute(
				"aria-label",
				this.buttonPreviousAriaLabel
			);
		}

		this.buttonNext.setAttribute("aria-hidden", this.buttonNextAriaHidden);
		this.buttonPrevious.setAttribute(
			"aria-hidden",
			this.buttonPreviousAriaLabel
		);
		if (this.buttonNextAriaHidden === "true") {
			this.buttonNext.setAttribute("tabindex", "-1");
		}
		if (this.buttonPreviousAriaHidden === "true") {
			this.buttonPrevious.setAttribute("tabindex", "-1");
		}

		this.buttonNext.setAttribute("class", this.buttonNextClasses);
		this.buttonPrevious.setAttribute("class", this.buttonPreviousClasses);

		this.slider.insertAdjacentElement("afterend", this.buttonNext);
		this.slider.insertAdjacentElement("afterend", this.buttonPrevious);

		this.buttonPrevious.setAttribute("hidden", "true");
		this.buttonNext.setAttribute("hidden", "true");
	}

	/**
	 * Setup click events on next / previous buttons
	 */
	setupButtons() {
		// Hide buttons on mobile devices
		if (this.isMobile) {
			this.buttonNext?.setAttribute("style", "display: none;");
			this.buttonPrevious?.setAttribute("style", "display: none;");
			return;
		}
		this.buttonNext?.addEventListener("click", this.next);
		this.buttonPrevious?.addEventListener("click", this.prev);
	}

	/**
	 * Creates progress indicator
	 */
	createProgressIndicator() {
		this.progress = document.createElement("div");
		this.progressBar = document.createElement("div");
		this.progressTrack = document.createElement("div");

		this.progress.classList.add("slide-list__progress");
		this.progressBar.classList.add("slide-list__progress__bar");
		this.progressTrack.classList.add("slide-list__progress__track");
		this.progressTrack.hidden = true;

		this.progressBar.appendChild(this.progressTrack);
		this.progress.appendChild(this.progressBar);
		this.slider.insertAdjacentElement("afterend", this.progress);
	}

	/**
	 * Updates progress indicator position
	 */
	updateSliderPos() {
		const scrollLeft = this.slider.scrollLeft;
		const maxScrollLeft = this.slider.scrollWidth - this.slider.clientWidth;

		// Calculate the progress of scrolling (0% at start, 100% at end)
		const trackerWidth =
			(this.slider.clientWidth / this.slider.scrollWidth) * 100;
		const trackerPos =
			trackerWidth * (this.slider.scrollLeft / this.slider.clientWidth);

		// Update the progress bar
		this.progressBar.style.transform = `translateX(${trackerPos}%)`;
		this.progressTrack.style.width = `${trackerWidth}%`;
		this.progressTrack.hidden = false;

		// Disable or enable the previous button if at the start
		if (scrollLeft <= 0) {
			this.buttonPrevious.disabled = true;
		} else {
			this.buttonPrevious.disabled = false;
		}

		// Disable or enable the next button if at the end
		if (scrollLeft >= maxScrollLeft) {
			this.buttonNext.disabled = true;
		} else {
			this.buttonNext.disabled = false;
		}
	}

	/**
	 * Next slide
	 */
	next() {
		this.goto(this.nearestIndex + 1);
	}

	/**
	 * Previous slide
	 */
	prev() {
		this.goto(this.nearestIndex - 1);
	}

	/**
	 * Goto selected slide
	 * @param {number} index - Index to go to.
	 */
	goto(index) {
		// Clamp within bounds
		const verifiedIndex = Math.max(
			0,
			Math.min(Math.round(index), this.sliderItems.length - 1)
		);
		const targetSlide = this.sliderItems[verifiedIndex];
		const left = targetSlide.offsetLeft;
		this.slider.scrollTo({
			top: 0,
			left,
			behavior: this.prefersReducedMotion ? "instant" : "smooth",
		});
	}

	/**
	 * helper function to throttle/debounce
	 * @param {function} f - Function to throttle.
	 * @param {number} delay - Delay time in milliseconds.
	 */
	// eslint-disable-next-line class-methods-use-this
	throttle(f, delay) {
		let timer = 0;
		// eslint-disable-next-line func-names
		return function(/** @type {any} */ ...args) {
			clearTimeout(timer);
			/** @ts-ignore */
			timer = setTimeout(() => f.apply(this, args), delay);
		};
	}
}

if (customElements.get("slide-list") === undefined) {
	window.customElements.define("slide-list", SlideList);
}
